import { Button } from "@blueprintjs/core";
import {
  ButtonsBloc,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  InlineButtonContainer,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  ERequestState,
  PersonApi,
  RequestApi,
  RequestDto,
  TitleRequestDto,
  TitlesRequestApi,
} from "../../../../api";
import {
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { RequestDogOwnerList } from "../../RequestDogOwnerList";

export interface ITitleRequestDetailsProps {
  titleRequestId: number;
  editMode: boolean;
}

export const TitleRequestDetails: React.FunctionComponent<ITitleRequestDetailsProps> = ({
  titleRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(TitlesRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const { currentLanguage } = useGlobalData();
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [dogIdValue, setDogIdValue] = React.useState(0);
  const [dogIdRequest, setDogIdRequest] = React.useState(true);
  const [dogBirthYear, setDogBirthYear] = React.useState(new Date().getFullYear());
  const [dogDeathYear, setDogDeathYear] = React.useState(new Date().getFullYear());

  const { data, loading, saving, saveItem, refresh } = useCrudApi(
    {
      getApiFn: React.useCallback(
        (id = titleRequestId) =>
          id > 0
            ? api.getTitleRequest(id)
            : new TitleRequestDto({
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        [api, titleRequestId]
      ),
      saveApiFn: React.useCallback(
        async (d: TitleRequestDto) => {
          const res = await api.save(d);
          return res;
        },
        [api]
      ),
      onSavedRoute: React.useCallback((d) => `${ERoutes.titleRequests}/${d.titleRequestId}/detail/edit`, []),
    },
    [titleRequestId]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (titleRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [titleRequestId, personApi, saveItem, showDialogPromise, t]
  );

  React.useEffect(() => {
    if (data && dogIdRequest) {
      setDogIdValue(data.dogId);
      setDogIdRequest(false);
    }
  }, [data, dogIdRequest]);

  const [titles, tloading] = useReferential(
    (a) => a.getBelgianTitles(currentLanguage.languageId, dogIdValue, data?.titleId),
    true,
    [dogIdValue]
  );

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (titleRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (titleRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      titleId: Yup.number().nullable().required(t(ETLCodes.Required)),
      year: Yup.string()
        .required(t(ETLCodes.Required))
        .matches(new RegExp("([0-9]){4}"), t(ETLCodes.YearIsNotCorrect))
        .test(
          "year-test",
          t(ETLCodes.YearMustBeBetweenBirthAndDeathError, {
            birthdate: dogBirthYear < new Date().getFullYear() - 20 ? new Date().getFullYear() - 20 : dogBirthYear,
            deathdate: dogDeathYear ?? new Date().getFullYear(),
          }),
          async function (value) {
            const currentDog = await dogApi.getDog(dogIdValue);
            setDogDeathYear(currentDog?.deathDate ? currentDog.deathDate.getFullYear() : new Date().getFullYear());
            setDogBirthYear(currentDog?.birthDate.getFullYear());
            if (this.parent.dogId && currentDog) {
              if (currentDog.deathDate) {
                return +value <= currentDog?.deathDate.getFullYear() && +value >= currentDog?.birthDate.getFullYear();
              } else {
                return +value >= currentDog?.birthDate.getFullYear() && +value <= new Date().getFullYear();
              }
            }
          }
        ),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (titleRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (titleRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data, dogApi, dogBirthYear, dogDeathYear, dogIdValue, personApi, t, titleRequestId]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  const [regenDiplomaLoading, setregenDiplomaLoading] = React.useState(false);

  const regenDiploma = React.useCallback(async () => {
    setregenDiplomaLoading(true);
    try {
      await api.regenAndReprintDiploma(titleRequestId);
      showSuccess(t(ETLCodes.Success));
    } catch {
      showError(t(ETLCodes.Error));
    }
    setregenDiplomaLoading(false);
  }, [api, t, titleRequestId]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.titleRequests)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="dogId" onChanged={(value) => setDogIdValue(value)} />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: (
              <>
                {titleRequestId > 0 && (
                  <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
                )}
                {data?.request?.status === ERequestState.EXECUTED ? (
                  <Button
                    loading={regenDiplomaLoading}
                    icon={"print"}
                    text={t(ETLCodes.RegenDiploma)}
                    onClick={regenDiploma}
                    minimal
                  />
                ) : null}
              </>
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              info={
                titleRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
              info={<InformationTooltip tooltipMessage={ETLCodes.CanBeOwnerOrOtherPersonInformation} />}
              dogId={dogIdValue}
              showWarning={true}
            />
          </div>

          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            rows={5}
            readonly={data?.request?.status === ERequestState.EXECUTED}
          />
        </FieldGroup>
        <FieldGroup fieldsetProps={{ title: t(ETLCodes.TitleData) }} columns={2}>
          <div>
            <FGSelectInput
              name="titleId"
              label={t(ETLCodes.Title)}
              items={titles}
              loading={tloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGTextInput
              name="year"
              label={t(ETLCodes.Year)}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={4}
            />
          </div>

          {data?.request?.status === ERequestState.EXECUTED && (
            <InlineButtonContainer>
              <div />
              <ButtonsBloc style={{ float: "right" }}>
                <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.titleRequests}`)} />
              </ButtonsBloc>
            </InlineButtonContainer>
          )}
        </FieldGroup>
        <RequestDogOwnerList dogId={dogIdValue} />
      </SmallFormGenerator>
    </>
  );
};
